import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Button,
  IconButton,
  Stack,
  Grid,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  ArrowBackIosNew,
  GridOn,
  VideoLibrary,
  Chat,
  Repeat,
  Send,
  Favorite,
  FavoriteBorder,
  ChatBubbleOutline,
  Bookmark,
  BookmarkBorder,
  IosShare,
  Close,
  LinkOutlined,
  WorkOutline,
  StarBorderOutlined,
  Verified,
  MoreVert,
  Block,
} from "@mui/icons-material";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  where,
  setDoc,
  serverTimestamp,
  updateDoc,
  arrayUnion,
  arrayRemove,
  increment,
} from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";
import { auth, db, storage } from "./firebase";
import Comentarios from "./Comentarios";
import PostTextBlock from "./PostTextBlock";
import { useTranslatedDictionary } from "./hooks/useTranslatedDictionary";

const PROFILE_USER_TEXT = {
  es: {
    user: "Usuario",
    posts: "Posts",
    followers: "Seguidores",
    following: "Seguidos",
    follow: "Seguir",
    requestFollow: "Solicitar seguimiento",
    followingBtn: "Siguiendo",
    message: "Mensaje",
    noText: "Sin texto",
    noPosts: "Este usuario aún no tiene publicaciones.",
    noVideoPosts: "Este usuario aún no tiene videos.",
    privateProfile: "Este perfil es privado. Sigue a este usuario para ver sus publicaciones.",
    sendToContacts: "Enviar a contactos",
    noContacts: "No sigues a ningun contacto todavia.",
    send: "Enviar",
    close: "Cerrar",
    peopleFollow: "Personas que lo siguen",
    peopleFollowing: "Personas que sigue",
    noUsers: "No hay usuarios para mostrar.",
    suggestions: "Sugerencias",
    suggestionsDesc: "Ordenadas por cantidad de seguidos en común",
    noSuggestions: "No hay sugerencias disponibles por ahora.",
    followed: "Seguido",
    mutualFollowing: "seguidos en común",
    pending: "Pendiente",
    userUnfollowed: "Ya no sigues a este usuario.",
    requestCancelled: "Solicitud cancelada.",
    requestSent: "Solicitud enviada.",
    followToggleError: "No se pudo actualizar el seguimiento.",
    userUnblocked: "Usuario desbloqueado.",
    userBlocked: "Usuario bloqueado.",
    blockToggleError: "No se pudo actualizar el bloqueo.",
    cannotMessageUser: "No puedes enviar mensajes a este usuario.",
    userDoesNotAcceptMessages: "Este usuario no acepta mensajes.",
    onlyMessagesFromFollowed: "Este usuario solo acepta mensajes de personas que sigue.",
    cannotInteract: "No puedes interactuar con este usuario.",
    unblockUser: "Desbloquear usuario",
    blockUser: "Bloquear usuario",
    cannotViewPosts: "No puedes ver las publicaciones de este usuario.",
    linkCopyError: "No se pudo copiar el enlace.",
    shareFallbackText: "Mira esta publicación en Gexvi",
    postImageAlt: "Publicación",
    sharedPostMessage: "Te compartió una publicación",
    usernameFallback: "usuario",
  },
  en: {
    user: "User",
    posts: "Posts",
    followers: "Followers",
    following: "Following",
    follow: "Follow",
    requestFollow: "Request follow",
    followingBtn: "Following",
    message: "Message",
    noText: "No text",
    noPosts: "This user has no posts yet.",
    noVideoPosts: "This user has no videos yet.",
    privateProfile: "This profile is private. Follow this user to see their posts.",
    sendToContacts: "Send to contacts",
    noContacts: "You are not following contacts yet.",
    send: "Send",
    close: "Close",
    peopleFollow: "People who follow this user",
    peopleFollowing: "People this user follows",
    noUsers: "No users to show.",
    suggestions: "Suggestions",
    suggestionsDesc: "Sorted by mutual following count",
    noSuggestions: "No suggestions available yet.",
    followed: "Followed",
    mutualFollowing: "mutual following",
    pending: "Pending",
    userUnfollowed: "You no longer follow this user.",
    requestCancelled: "Request cancelled.",
    requestSent: "Request sent.",
    followToggleError: "Could not update the follow status.",
    userUnblocked: "User unblocked.",
    userBlocked: "User blocked.",
    blockToggleError: "Could not update the block status.",
    cannotMessageUser: "You cannot send messages to this user.",
    userDoesNotAcceptMessages: "This user does not accept messages.",
    onlyMessagesFromFollowed: "This user only accepts messages from people they follow.",
    cannotInteract: "You cannot interact with this user.",
    unblockUser: "Unblock user",
    blockUser: "Block user",
    cannotViewPosts: "You cannot see this user's posts.",
    linkCopyError: "Could not copy the link.",
    shareFallbackText: "Check out this post on Gexvi",
    postImageAlt: "Post",
    sharedPostMessage: "Shared a post with you",
    usernameFallback: "user",
  },
};

const PerfilUsuario = ({ uid, onBack, onOpenChat, onOpenProfile, language = "es", themeMode = "oscuro" }) => {
  const t = useTranslatedDictionary(PROFILE_USER_TEXT, language);
  const isLightTheme = themeMode === "claro";
  const onSurface = (opacity = 1) =>
    isLightTheme ? `rgba(15,20,32,${opacity})` : `rgba(255,255,255,${opacity})`;
  const dialogBg = isLightTheme ? "#ffffff" : "#0b1016";
  const dialogGradient = isLightTheme
    ? "radial-gradient(circle at top, rgba(0,150,180,0.06) 0%, rgba(255,255,255,1) 55%)"
    : "radial-gradient(circle at top, rgba(0,212,255,0.16) 0%, rgba(11,16,22,1) 48%)";
  const getPrimaryMedia = (post) => {
    const mediaItems = Array.isArray(post?.media) ? post.media : [];
    const videoItem = mediaItems.find((item) => item?.type === "video") || mediaItems.find((item) => /\.(mp4|webm|ogg)(\?|$)/i.test(item?.url || ""));
    if (videoItem) return videoItem;
    const imageItem = mediaItems.find((item) => item?.type === "image") || mediaItems[0];
    if (imageItem) return imageItem;
    if (post?.videoUrl) return { type: "video", url: post.videoUrl };
    if (post?.imageUrl) return { type: "image", url: post.imageUrl };
    return null;
  };

  const postHasVideo = (post) => {
    const mediaItems = Array.isArray(post?.media) ? post.media : [];
    return Boolean(post?.videoUrl) || mediaItems.some((item) => item?.type === "video" || /\.(mp4|webm|ogg)(\?|$)/i.test(item?.url || ""));
  };

  const postHasImage = (post) => {
    const mediaItems = Array.isArray(post?.media) ? post.media : [];
    return Boolean(post?.imageUrl) || mediaItems.some((item) => item?.type === "image");
  };

  const currentUser = auth.currentUser;
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState("imagenes");
  const [allUsers, setAllUsers] = useState([]);
  const [postProfilesByUid, setPostProfilesByUid] = useState({});
  const [resolvedPhotosByUid, setResolvedPhotosByUid] = useState({});
  const [userPosts, setUserPosts] = useState([]);
  const [following, setFollowing] = useState(false);
  const [myFollowingIds, setMyFollowingIds] = useState([]);
  const [myBlockedUids, setMyBlockedUids] = useState([]);
  const [blockLoading, setBlockLoading] = useState(false);
  const [pendingFollowRequestIds, setPendingFollowRequestIds] = useState([]);
  const [followLoadingUid, setFollowLoadingUid] = useState("");
  const [openConnectionsDialog, setOpenConnectionsDialog] = useState(false);
  const [connectionsTab, setConnectionsTab] = useState("followers");
  const [selectedPost, setSelectedPost] = useState(null);
  const [openPostDialog, setOpenPostDialog] = useState(false);
  const [openComentariosPost, setOpenComentariosPost] = useState(false);
  const [openSendPostDialog, setOpenSendPostDialog] = useState(false);
  const [sendingPostUid, setSendingPostUid] = useState("");
  const [toast, setToast] = useState({ open: false, type: "success", text: "" });
  const [optionsMenuAnchor, setOptionsMenuAnchor] = useState(null);

  // Cargar perfil del usuario
  useEffect(() => {
    if (!uid) return;
    const userRef = doc(db, "users", uid);
    const unsubscribe = onSnapshot(userRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data() || {};
        const rawWebsites = data.websites ?? data.website ?? [];
        const websites = Array.isArray(rawWebsites)
          ? rawWebsites.map(String).filter(Boolean)
          : rawWebsites
            ? [String(rawWebsites)]
            : [];

        setProfile({
          uid,
          ...data,
          websites,
          bio: data.bio || "",
          employment: typeof data.employment === "string" ? data.employment : "",
          position: typeof data.position === "string" ? data.position : "",
          interests: typeof data.interests === "string" ? data.interests : "",
        });
      } else {
        // Si no tiene doc en users, intentar armar perfil desde posts
        setProfile({
          uid,
          displayName: t("user"),
          photoURL: "",
          bio: "",
          websites: [],
          employment: "",
          position: "",
          interests: "",
        });
      }
    });
    return () => unsubscribe();
  }, [uid, t]);

  // Verificar si el usuario actual ya sigue a este usuario
  useEffect(() => {
    if (!currentUser || !uid) return;
    const myRef = doc(db, "users", currentUser.uid);
    const unsubscribe = onSnapshot(myRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        const followingList = Array.isArray(data.following) ? data.following : [];
        setMyFollowingIds(followingList);
        setFollowing(followingList.includes(uid));
        setMyBlockedUids(Array.isArray(data.blockedUids) ? data.blockedUids : []);
      }
    });
    return () => unsubscribe();
  }, [currentUser, uid]);

  // Cargar usuarios para seguidores/seguidos/sugerencias
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "users"), (snap) => {
      setAllUsers(snap.docs.map((d) => ({ uid: d.id, ...d.data() })));
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "posts"), (snap) => {
      const profileMap = {};
      snap.docs.forEach((d) => {
        const post = d.data() || {};
        if (!post.uid) return;
        const current = profileMap[post.uid] || {};
        profileMap[post.uid] = {
          displayName: current.displayName || post.username || "",
          handle: current.handle || post.handle || "",
          photoURL: current.photoURL || post.userImg || post.photoURL || post.photoUrl || "",
        };
      });
      setPostProfilesByUid(profileMap);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const getRawPhoto = (user) => {
      const postProfile = postProfilesByUid[user?.uid] || {};
      return (
        user?.photoURL ||
        user?.photoUrl ||
        user?.userImg ||
        user?.avatar ||
        postProfile.photoURL ||
        ""
      ).trim();
    };

    const needsStorageResolution = (value) => {
      if (!value) return false;
      const isHttp = /^https?:\/\//i.test(value);
      const isDataOrBlob = /^(data:|blob:|\/)/i.test(value);
      return !isHttp && !isDataOrBlob;
    };

    const resolvePhotos = async () => {
      const candidates = allUsers
        .map((u) => ({ uid: u.uid, raw: getRawPhoto(u) }))
        .filter((item) => item.uid && item.raw && !resolvedPhotosByUid[item.uid]);

      if (!candidates.length) return;

      const entries = {};
      await Promise.all(
        candidates.map(async ({ uid: candidateUid, raw }) => {
          let finalUrl = raw;
          if (needsStorageResolution(raw)) {
            try {
              finalUrl = await getDownloadURL(ref(storage, raw));
            } catch {
              finalUrl = raw;
            }
          }
          entries[candidateUid] = finalUrl;
        })
      );

      if (!cancelled && Object.keys(entries).length > 0) {
        setResolvedPhotosByUid((prev) => ({ ...prev, ...entries }));
      }
    };

    resolvePhotos();
    return () => {
      cancelled = true;
    };
  }, [allUsers, postProfilesByUid, resolvedPhotosByUid]);

  // Cargar posts del usuario
  useEffect(() => {
    if (!uid) return;
    const q = query(collection(db, "posts"), where("uid", "==", uid));
    const unsubscribe = onSnapshot(q, (snap) => {
      const items = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b?.timestamp?.seconds || 0) - (a?.timestamp?.seconds || 0));
      setUserPosts(items);
    });
    return () => unsubscribe();
  }, [uid]);

  // Cargar solicitudes de seguimiento pendientes del usuario actual
  useEffect(() => {
    if (!currentUser) return;
    const q = query(
      collection(db, "follow_requests"),
      where("requesterUid", "==", currentUser.uid),
      where("status", "==", "pending")
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      const pending = snap.docs
        .map((d) => d.data().targetUid)
        .filter(Boolean);
      setPendingFollowRequestIds(pending);
    });
    return () => unsubscribe();
  }, [currentUser]);

  const toggleFollowUser = async (targetUid) => {
    if (!currentUser || !targetUid || followLoadingUid) return;
    const isFollowingNow = myFollowingIds.includes(targetUid);
    const isPending = pendingFollowRequestIds.includes(targetUid);
    setFollowLoadingUid(targetUid);
    
    try {
      const myRef = doc(db, "users", currentUser.uid);
      const requestId = `follow_request_${currentUser.uid}_${targetUid}`;

      if (isFollowingNow) {
        // Dejar de seguir (el contador de seguidores del otro lo recalcula el backend)
        await updateDoc(myRef, {
          following: arrayRemove(targetUid),
          followingCount: increment(-1),
        });
        setToast({ open: true, type: "success", text: t("userUnfollowed") });
      } else if (isPending) {
        // Cancelar solicitud pendiente
        await setDoc(
          doc(db, "follow_requests", requestId),
          { status: "cancelled", updatedAt: serverTimestamp() },
          { merge: true }
        );
        setToast({ open: true, type: "success", text: t("requestCancelled") });
      } else {
        // Crear nueva solicitud de seguimiento
        const requesterName = currentUser.displayName || currentUser.email?.split("@")[0] || t("user");
        const requesterPhoto = currentUser.photoURL || "";

        // Crear solicitud de seguimiento
        await setDoc(
          doc(db, "follow_requests", requestId),
          {
            type: "follow_request",
            status: "pending",
            requesterUid: currentUser.uid,
            targetUid: targetUid,
            requesterName: requesterName,
            requesterPhotoURL: requesterPhoto,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        // Enviar notificación al usuario objetivo
        const notificationId = `follow_request_${currentUser.uid}_${Date.now()}`;
        await setDoc(
          doc(db, "users", targetUid, "notifications", notificationId),
          {
            type: "follow_request",
            actorUid: currentUser.uid,
            authorUid: currentUser.uid,
            actorPhotoURL: requesterPhoto,
            requesterPhotoURL: requesterPhoto,
            title: `${requesterName} te solicita seguir`,
            body: `${requesterName} te solicita permiso para verte.`,
            status: "pending",
            read: false,
            createdAt: serverTimestamp(),
          }
        );

        setToast({ open: true, type: "success", text: t("requestSent") });
      }
    } catch (e) {
      console.error(e);
      setToast({ open: true, type: "error", text: t("followToggleError") });
    } finally {
      setFollowLoadingUid("");
    }
  };

  const handleFollow = async () => {
    await toggleFollowUser(uid);
  };

  const iBlockedThem = myBlockedUids.includes(uid);
  const theyBlockedMe = Array.isArray(profile?.blockedUids) && profile.blockedUids.includes(currentUser?.uid);

  const handleToggleBlock = async () => {
    if (!currentUser || !uid || blockLoading) return;
    setBlockLoading(true);
    try {
      const myRef = doc(db, "users", currentUser.uid);
      if (iBlockedThem) {
        await updateDoc(myRef, { blockedUids: arrayRemove(uid) });
        setToast({ open: true, type: "success", text: t("userUnblocked") });
      } else {
        await updateDoc(myRef, { blockedUids: arrayUnion(uid) });
        if (myFollowingIds.includes(uid)) {
          await updateDoc(myRef, { following: arrayRemove(uid), followingCount: increment(-1) });
        }
        setToast({ open: true, type: "success", text: t("userBlocked") });
      }
    } catch (e) {
      console.error(e);
      setToast({ open: true, type: "error", text: t("blockToggleError") });
    } finally {
      setBlockLoading(false);
    }
  };

  const handleMessageClick = () => {
    if (!onOpenChat) return;
    if (theyBlockedMe || iBlockedThem) {
      setToast({ open: true, type: "error", text: t("cannotMessageUser") });
      return;
    }
    const mensajesDe = profile?.mensajesDe || "todos";
    if (mensajesDe === "nadie") {
      setToast({ open: true, type: "error", text: t("userDoesNotAcceptMessages") });
      return;
    }
    if (mensajesDe === "seguidos") {
      const theyFollowMe = Array.isArray(profile?.following) && profile.following.includes(currentUser?.uid);
      if (!theyFollowMe) {
        setToast({ open: true, type: "error", text: t("onlyMessagesFromFollowed") });
        return;
      }
    }
    onOpenChat(profile);
  };

  const getFollowLabel = (targetUid) => {
    if (myFollowingIds.includes(targetUid)) {
      return t("followingBtn");
    }
    if (pendingFollowRequestIds.includes(targetUid)) {
      return t("pending");
    }
    return t("follow");
  };

  const getFollowButtonColor = (targetUid) => {
    if (myFollowingIds.includes(targetUid)) {
      return { bg: onSurface(0.1), color: onSurface(1) };
    }
    if (pendingFollowRequestIds.includes(targetUid)) {
      return { bg: "rgba(255,200,0,0.2)", color: "#ffc800" };
    }
    return { bg: "#00d4ff", color: "#000" };
  };

  const followingUsers = useMemo(() => {
    const ids = Array.isArray(profile?.following) ? profile.following : [];
    return ids.map((followUid) => {
      const found = allUsers.find((u) => u.uid === followUid);
      if (found) return found;
      return { uid: followUid, displayName: t("user"), photoURL: "", handle: "" };
    });
  }, [allUsers, profile?.following]); // eslint-disable-line react-hooks/exhaustive-deps

  const followerUsers = useMemo(() => {
    return allUsers.filter((u) => Array.isArray(u.following) && u.following.includes(uid));
  }, [allUsers, uid]);

  const suggestions = useMemo(() => {
    if (!currentUser) return [];
    const myFollowingSet = new Set(myFollowingIds || []);

    return allUsers
      .filter((u) => u.uid !== currentUser.uid && u.uid !== uid)
      .map((u) => {
        const candidateFollowing = Array.isArray(u.following) ? u.following : [];
        let mutualCount = 0;
        candidateFollowing.forEach((id) => {
          if (myFollowingSet.has(id)) mutualCount += 1;
        });
        return { ...u, mutualCount };
      })
      .sort((a, b) => {
        if (b.mutualCount !== a.mutualCount) return b.mutualCount - a.mutualCount;
        return (b.followersCount || 0) - (a.followersCount || 0);
      })
      .slice(0, 10);
  }, [allUsers, currentUser, myFollowingIds, uid]);

  const activeConnections = connectionsTab === "followers" ? followerUsers : followingUsers;

  const followingContacts = useMemo(() => {
    return myFollowingIds
      .map((followUid) => {
        const found = allUsers.find((u) => u.uid === followUid);
        if (found) return found;
        return { uid: followUid, displayName: t("user"), photoURL: "", handle: "" };
      })
      .filter((item) => item.uid && item.uid !== currentUser?.uid);
  }, [allUsers, myFollowingIds, currentUser?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolveUserIdentity = (item) => {
    const postProfile = postProfilesByUid[item?.uid] || {};
    const rawPhoto = item?.photoURL || item?.photoUrl || item?.userImg || item?.avatar || postProfile.photoURL || "";
    return {
      name: item?.displayName || item?.username || postProfile.displayName || item?.handle || postProfile.handle || t("user"),
      handle: item?.username || item?.handle || postProfile.username || postProfile.handle || "",
      photo: resolvedPhotosByUid[item?.uid] || rawPhoto,
    };
  };

  const handleOpenUserProfile = (targetUid) => {
    if (!targetUid || !onOpenProfile) return;
    setOpenConnectionsDialog(false);
    onOpenProfile(targetUid);
  };

  const handleRepost = async () => {
    if (!currentUser || !selectedPost) return;
    try {
      const userRef = doc(db, "users", currentUser.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.exists() ? userSnap.data() : {};
      const repostMedia = Array.isArray(selectedPost.media)
        ? selectedPost.media
        : [
            ...(selectedPost.imageUrl ? [{ type: "image", url: selectedPost.imageUrl }] : []),
            ...(selectedPost.videoUrl ? [{ type: "video", url: selectedPost.videoUrl }] : []),
          ];
      const repostImageUrl =
        repostMedia.find((item) => item?.type === "image")?.url ||
        selectedPost.imageUrl ||
        "";
      const repostVideoUrl =
        repostMedia.find((item) => item?.type === "video")?.url ||
        selectedPost.videoUrl ||
        "";
      await addDoc(collection(db, "posts"), {
        uid: currentUser.uid,
        username: userData.displayName || currentUser.displayName || t("user"),
        handle: userData.username || currentUser.email?.split("@")[0] || t("usernameFallback"),
        userImg: userData.photoURL || currentUser.photoURL || "",
        text: selectedPost.text || "",
        media: repostMedia,
        imageUrl: repostImageUrl,
        videoUrl: repostVideoUrl,
        likes: [],
        savedBy: [],
        commentsCount: 0,
        isRepost: true,
        originalPostId: selectedPost.id,
        timestamp: serverTimestamp(),
      });
      await updateDoc(doc(db, "posts", selectedPost.id), { repostCount: increment(1) });
    } catch (error) {
      console.error("No se pudo republicar", error);
    }
  };

  const handleSendPostToContact = async (targetUid) => {
    if (!currentUser || !selectedPost || !targetUid || sendingPostUid) return;
    setSendingPostUid(targetUid);
    try {
      const targetIdentity = resolveUserIdentity({ uid: targetUid });
      const myDisplayName = currentUser.displayName || currentUser.email?.split("@")[0] || "Yo";
      const myPhoto = currentUser.photoURL || "";
      const chatId = [currentUser.uid, targetUid].sort().join("_");

      await setDoc(
        doc(db, "chats", chatId),
        {
          participants: [currentUser.uid, targetUid],
          members: [currentUser.uid, targetUid],
          participantNames: {
            [currentUser.uid]: myDisplayName,
            [targetUid]: targetIdentity.name,
          },
          participantPhotos: {
            [currentUser.uid]: myPhoto,
            [targetUid]: targetIdentity.photo,
          },
          lastMessage: t("sharedPostMessage"),
          lastMessageSenderUid: currentUser.uid,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await addDoc(collection(db, "chats", chatId, "messages"), {
        uid: currentUser.uid,
        text: `${t("sharedPostMessage")}: ${selectedPost.text || t("shareFallbackText")}`,
        sharedPostId: selectedPost.id,
        sharedPostText: selectedPost.text || "",
        sharedPostImage: selectedPost.imageUrl || "",
        timestamp: serverTimestamp(),
      });

      await updateDoc(doc(db, "posts", selectedPost.id), { sentCount: increment(1) });
      setOpenSendPostDialog(false);
    } catch (error) {
      console.error("No se pudo enviar la publicacion", error);
    } finally {
      setSendingPostUid("");
    }
  };

  const handlePostClick = (post) => {
    setSelectedPost(post);
    setOpenPostDialog(true);
  };

  const handleLikePost = async () => {
    if (!currentUser || !selectedPost) return;
    const postRef = doc(db, "posts", selectedPost.id);
    const liked = selectedPost.likes?.includes(currentUser.uid);
    await updateDoc(postRef, { likes: liked ? arrayRemove(currentUser.uid) : arrayUnion(currentUser.uid) });
  };

  const handleSavePost = async () => {
    if (!currentUser || !selectedPost) return;
    const postRef = doc(db, "posts", selectedPost.id);
    const saved = selectedPost.savedBy?.includes(currentUser.uid);
    await updateDoc(postRef, { savedBy: saved ? arrayRemove(currentUser.uid) : arrayUnion(currentUser.uid) });
  };

  const handleSharePost = async (post) => {
    const text = post?.text ? post.text.substring(0, 100) : t("shareFallbackText");
    const shareUrl = window.location.href;

    const copyToClipboard = (value) => {
      try {
        const el = document.createElement("textarea");
        el.value = value;
        el.style.position = "fixed";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.focus();
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        return true;
      } catch {
        return false;
      }
    };

    if (navigator.share) {
      try {
        await navigator.share({ title: "Gexvi", text, url: shareUrl });
        return;
      } catch {
        // Si el usuario cancela, continúa con copia
      }
    }

    let copied = false;
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        copied = true;
      } catch {
        copied = copyToClipboard(shareUrl);
      }
    } else {
      copied = copyToClipboard(shareUrl);
    }

    if (!copied) {
      alert(t("linkCopyError"));
    }
  };

  if (!profile) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", bgcolor: isLightTheme ? "#ffffff" : "#000" }}>
        <CircularProgress sx={{ color: "#00d4ff" }} />
      </Box>
    );
  }

  const isOwnProfile = currentUser?.uid === uid;
  const isPublicProfile = profile?.preferences?.publicProfile !== false;
  const isBlockedEitherWay = iBlockedThem || theyBlockedMe;
  const canViewPosts = !isBlockedEitherWay && (isOwnProfile || isPublicProfile || following);
  const canSeeFollowCounts = isOwnProfile || profile?.mostrarSeguidores !== false;

  return (
    <Box
      sx={{
        color: onSurface(1),
        minHeight: "100dvh",
        width: "100%",
        overflowX: "hidden",
        pb: { xs: 11, sm: 10 },
        background:
          isLightTheme
            ? "radial-gradient(circle at top, rgba(0,150,180,0.08) 0%, #ffffff 40%, #f2f5f8 100%)"
            : "radial-gradient(circle at top, rgba(0, 212, 255, 0.12) 0%, rgba(0,0,0,0.95) 32%, #000 100%)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "sticky",
          top: 0,
          bgcolor: isLightTheme ? "rgba(255,255,255,0.82)" : "rgba(0,0,0,0.82)",
          backdropFilter: "blur(10px)",
          zIndex: 10,
        }}
      >
        <IconButton onClick={onBack} sx={{ color: onSurface(1) }}>
          <ArrowBackIosNew />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.6 }}>
            {profile.displayName || t("user")}
            {profile.verified ? <Verified sx={{ fontSize: 18, color: "#35b6ff" }} /> : null}
          </Box>
        </Typography>
        {currentUser && currentUser.uid !== uid ? (
          <>
            <IconButton onClick={(e) => setOptionsMenuAnchor(e.currentTarget)} sx={{ color: onSurface(1) }}>
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={optionsMenuAnchor}
              open={Boolean(optionsMenuAnchor)}
              onClose={() => setOptionsMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setOptionsMenuAnchor(null);
                  handleToggleBlock();
                }}
                disabled={blockLoading}
                sx={{ color: iBlockedThem ? "#00d4ff" : "#ff6b6b", gap: 1 }}
              >
                <Block fontSize="small" />
                {iBlockedThem ? t("unblockUser") : t("blockUser")}
              </MenuItem>
            </Menu>
          </>
        ) : (
          <Box sx={{ width: 40 }} />
        )}
      </Box>

      {/* Info */}
      <Box sx={{ px: { xs: 2, sm: 3 }, mt: 2, maxWidth: "860px", mx: "auto" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 2, sm: 4 }}
          alignItems="center"
          sx={{ mb: 3 }}
        >
          <Avatar
            src={profile.photoURL}
            sx={{
              width: { xs: 82, sm: 94 },
              height: { xs: 82, sm: 94 },
              border: "2px solid #00d4ff",
              boxShadow: "0 0 0 4px rgba(0,212,255,0.12)",
            }}
          />
          <Stack
            direction="row"
            spacing={{ xs: 2, sm: 3 }}
            flex={1}
            justifyContent={{ xs: "space-between", sm: "center" }}
            width="100%"
          >
            <Box textAlign="center">
              <Typography variant="h6" sx={{ color: onSurface(1), fontWeight: "bold" }}>
                {userPosts.length}
              </Typography>
              <Typography variant="caption" sx={{ color: onSurface(0.6) }}>
                {t("posts")}
              </Typography>
            </Box>
            <Box
              textAlign="center"
              onClick={() => {
                if (!canSeeFollowCounts) return;
                setConnectionsTab("followers");
                setOpenConnectionsDialog(true);
              }}
              sx={{ cursor: canSeeFollowCounts ? "pointer" : "default" }}
            >
              <Typography variant="h6" sx={{ color: onSurface(1), fontWeight: "bold" }}>
                {canSeeFollowCounts ? (profile.followersCount || 0) : "—"}
              </Typography>
              <Typography variant="caption" sx={{ color: onSurface(0.6) }}>
                {t("followers")}
              </Typography>
            </Box>
            <Box
              textAlign="center"
              onClick={() => {
                if (!canSeeFollowCounts) return;
                setConnectionsTab("following");
                setOpenConnectionsDialog(true);
              }}
              sx={{ cursor: canSeeFollowCounts ? "pointer" : "default" }}
            >
              <Typography variant="h6" sx={{ color: onSurface(1), fontWeight: "bold" }}>
                {canSeeFollowCounts ? (profile.followingCount || 0) : "—"}
              </Typography>
              <Typography variant="caption" sx={{ color: onSurface(0.6) }}>
                {t("following")}
              </Typography>
            </Box>
          </Stack>
        </Stack>

        <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: onSurface(1) }}>
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.6 }}>
            {profile.displayName || t("user")}
            {profile.verified ? <Verified sx={{ fontSize: 17, color: "#35b6ff" }} /> : null}
          </Box>
        </Typography>
        {profile.bio && (
          <Typography
            variant="body2"
            sx={{
              color: onSurface(0.8),
              mb: profile.websites?.length || profile.employment || profile.position || profile.interests ? 1.5 : 3,
              maxWidth: "620px",
            }}
          >
            {profile.bio}
          </Typography>
        )}

        {(profile.employment || profile.position || (profile.websites && profile.websites.length > 0) || profile.interests) && (
          <Box sx={{ mb: 2.5, display: "flex", flexDirection: "column", gap: 0.8 }}>
            {(profile.employment || profile.position) && (
              <Stack direction="row" spacing={1} alignItems="center">
                <WorkOutline sx={{ fontSize: 16, color: onSurface(0.45) }} />
                <Typography variant="body2" sx={{ color: onSurface(0.75) }}>
                  {[profile.position, profile.employment].filter(Boolean).join(" en ")}
                </Typography>
              </Stack>
            )}

            {profile.websites && profile.websites.length > 0 && (
              <Stack direction="column" spacing={0.5}>
                {profile.websites.map((url, i) => (
                  <Stack key={`${url}-${i}`} direction="row" spacing={1} alignItems="center">
                    <LinkOutlined sx={{ fontSize: 16, color: onSurface(0.45) }} />
                    <Typography
                      component="a"
                      href={url.startsWith("http") ? url : `https://${url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      sx={{ color: "#00d4ff", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                    >
                      {url.replace(/^https?:\/\//, "")}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}

            {profile.interests && (
              <Stack direction="row" spacing={1} alignItems="center">
                <StarBorderOutlined sx={{ fontSize: 16, color: onSurface(0.45) }} />
                <Typography variant="body2" sx={{ color: onSurface(0.75) }}>
                  {profile.interests}
                </Typography>
              </Stack>
            )}
          </Box>
        )}

        {/* Botones */}
        {currentUser && currentUser.uid !== uid && (
          <Stack spacing={1} sx={{ mb: 4, mt: profile.bio || profile.employment || profile.position || profile.interests || profile.websites?.length ? 0 : 2 }}>
            {theyBlockedMe ? (
              <Typography sx={{ color: onSurface(0.6), fontSize: "0.85rem" }}>
                {t("cannotInteract")}
              </Typography>
            ) : (
              <Stack direction="row" spacing={1}>
                {!iBlockedThem && (
                  <>
                    <Button
                      variant="contained"
                      fullWidth
                      disabled={followLoadingUid === uid}
                      onClick={handleFollow}
                      sx={{
                        bgcolor: getFollowButtonColor(uid).bg,
                        color: getFollowButtonColor(uid).color,
                        textTransform: "none",
                        borderRadius: "12px",
                        fontWeight: 600,
                        "&:hover": {
                          bgcolor: pendingFollowRequestIds.includes(uid) ? "rgba(255,200,0,0.3)" : (following ? onSurface(0.2) : "#00b8d9")
                        },
                      }}
                    >
                      {followLoadingUid === uid ? <CircularProgress size={18} /> : getFollowLabel(uid)}
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<Chat />}
                      onClick={handleMessageClick}
                      sx={{
                        color: onSurface(1),
                        borderColor: onSurface(0.3),
                        textTransform: "none",
                        borderRadius: "12px",
                        "&:hover": { borderColor: "#00d4ff", bgcolor: "rgba(0,212,255,0.08)" },
                      }}
                    >
                      {t("message")}
                    </Button>
                  </>
                )}
              </Stack>
            )}
          </Stack>
        )}
      </Box>

      <Divider sx={{ bgcolor: onSurface(0.1) }} />

      {canViewPosts ? (
        <>
          {/* Tab posts */}
          <Stack direction="row" justifyContent="space-around" sx={{ py: 1, maxWidth: "860px", mx: "auto" }}>
            <Box
              onClick={() => setActiveTab("imagenes")}
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                borderBottom: activeTab === "imagenes" ? "2px solid #00d4ff" : "2px solid transparent",
                py: 0.5,
                cursor: "pointer",
              }}
            >
              <GridOn sx={{ color: activeTab === "imagenes" ? "#00d4ff" : onSurface(0.5) }} />
            </Box>
            <Box
              onClick={() => setActiveTab("videos")}
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                borderBottom: activeTab === "videos" ? "2px solid #00d4ff" : "2px solid transparent",
                py: 0.5,
                cursor: "pointer",
              }}
            >
              <VideoLibrary sx={{ color: activeTab === "videos" ? "#00d4ff" : onSurface(0.5) }} />
            </Box>
          </Stack>

          {/* Grid de posts */}
          {(() => {
            const imagesTabPosts = userPosts.filter((post) => postHasImage(post) || (!postHasVideo(post) && !postHasImage(post)));
            const videosTabPosts = userPosts.filter((post) => postHasVideo(post));
            const visiblePosts = activeTab === "videos" ? videosTabPosts : imagesTabPosts;
            return (
              <>
          <Grid container spacing={0.75} sx={{ px: { xs: 1, sm: 2 }, maxWidth: "860px", mx: "auto" }}>
            {visiblePosts.map((post) => (
              <Grid size={4} key={post.id}>
                <Box
                  onClick={() => handlePostClick(post)}
                  sx={{
                    aspectRatio: "1/1",
                    bgcolor: onSurface(0.04),
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    cursor: "pointer",
                    "&:hover": { opacity: 0.82, transform: "scale(0.98)", transition: "all 0.15s" },
                  }}
                >
                  {getPrimaryMedia(post) ? (() => {
                    const primaryMedia = getPrimaryMedia(post);
                    const isVideo = primaryMedia?.type === 'video' || /\.(mp4|webm|ogg)(\?|$)/i.test(primaryMedia?.url || '');
                    return (
                      <Box
                        component={isVideo ? 'video' : 'img'}
                        src={primaryMedia?.url || ''}
                        controls={Boolean(isVideo)}
                        muted={Boolean(isVideo)}
                        playsInline={Boolean(isVideo)}
                        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    );
                  })() : (
                    <Typography
                      variant="caption"
                      sx={{
                        color: onSurface(0.62),
                        px: 1,
                        textAlign: "center",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {post.text || t("noText")}
                    </Typography>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>

          {visiblePosts.length === 0 && (
            <Typography sx={{ color: onSurface(0.6), textAlign: "center", mt: 3 }}>
              {activeTab === "videos" ? t("noVideoPosts") : t("noPosts")}
            </Typography>
          )}
              </>
            );
          })()}
        </>
      ) : (
        <Box sx={{ maxWidth: "860px", mx: "auto", px: { xs: 2, sm: 3 }, py: 3.5 }}>
          <Typography sx={{ color: onSurface(0.78), textAlign: "center" }}>
            {isBlockedEitherWay ? t("cannotViewPosts") : t("privateProfile")}
          </Typography>
        </Box>
      )}

      <Dialog
        open={openConnectionsDialog}
        onClose={() => setOpenConnectionsDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            borderRadius: "16px",
            backgroundImage: dialogGradient,
            overflow: "hidden",
            maxHeight: "84vh",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", px: 1.5, py: 1, borderBottom: `1px solid ${onSurface(0.08)}` }}>
          <Stack direction="row" spacing={0.5}>
            <Button
              onClick={() => setConnectionsTab("followers")}
              sx={{
                textTransform: "none",
                minWidth: 100,
                color: connectionsTab === "followers" ? "#00d4ff" : onSurface(0.7),
                borderBottom: connectionsTab === "followers" ? "2px solid #00d4ff" : "2px solid transparent",
                borderRadius: 0,
              }}
            >
              {t("followers")}
            </Button>
            <Button
              onClick={() => setConnectionsTab("following")}
              sx={{
                textTransform: "none",
                minWidth: 100,
                color: connectionsTab === "following" ? "#00d4ff" : onSurface(0.7),
                borderBottom: connectionsTab === "following" ? "2px solid #00d4ff" : "2px solid transparent",
                borderRadius: 0,
              }}
            >
              {t("following")}
            </Button>
          </Stack>
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={() => setOpenConnectionsDialog(false)} sx={{ color: onSurface(0.7) }}>
            <Close />
          </IconButton>
        </Box>

        <Box sx={{ px: 2, py: 1.5, overflowY: "auto" }}>
          <Typography variant="caption" sx={{ color: onSurface(0.5), letterSpacing: 0.3 }}>
            {connectionsTab === "followers" ? t("peopleFollow") : t("peopleFollowing")}
          </Typography>

          <Stack spacing={1} sx={{ mt: 1.2 }}>
            {activeConnections.length === 0 && (
              <Typography sx={{ color: onSurface(0.55), py: 1.5 }}>
                {t("noUsers")}
              </Typography>
            )}

            {activeConnections.map((item) => {
              const isMe = currentUser?.uid === item.uid;
              const iFollow = myFollowingIds.includes(item.uid);
              const identity = resolveUserIdentity(item);
              return (
                <Box
                  key={`${connectionsTab}-${item.uid}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    p: 1,
                    borderRadius: "12px",
                    bgcolor: onSurface(0.03),
                    border: `1px solid ${onSurface(0.07)}`,
                  }}
                >
                  <Avatar
                    src={identity.photo}
                    sx={{ width: 40, height: 40, cursor: "pointer" }}
                    onClick={() => handleOpenUserProfile(item.uid)}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="subtitle2"
                      noWrap
                      sx={{ fontWeight: 700, cursor: "pointer" }}
                      onClick={() => handleOpenUserProfile(item.uid)}
                    >
                      {identity.name}
                    </Typography>
                    <Typography variant="caption" noWrap sx={{ color: onSurface(0.55), display: "block" }}>
                      @{identity.handle || t("usernameFallback")}
                    </Typography>
                  </Box>
                  {!isMe && (
                    <Button
                      onClick={() => toggleFollowUser(item.uid)}
                      disabled={followLoadingUid === item.uid}
                      sx={{
                        textTransform: "none",
                        minWidth: 90,
                        borderRadius: "10px",
                        fontWeight: 600,
                        bgcolor: iFollow ? onSurface(0.1) : "#00d4ff",
                        color: iFollow ? onSurface(1) : "#001217",
                        "&:hover": {
                          bgcolor: iFollow ? onSurface(0.18) : "#00b8d9",
                        },
                      }}
                    >
                      {followLoadingUid === item.uid ? <CircularProgress size={16} /> : iFollow ? t("followed") : getFollowLabel(item.uid)}
                    </Button>
                  )}
                </Box>
              );
            })}
          </Stack>

          <Divider sx={{ bgcolor: onSurface(0.08), my: 2 }} />

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {t("suggestions")}
          </Typography>
          <Typography variant="caption" sx={{ color: onSurface(0.5), display: "block", mb: 1.2 }}>
            {t("suggestionsDesc")}
          </Typography>

          <Stack spacing={1}>
            {suggestions.length === 0 && (
              <Typography sx={{ color: onSurface(0.55), py: 1.5 }}>
                {t("noSuggestions")}
              </Typography>
            )}

            {suggestions.map((item) => {
              const iFollow = myFollowingIds.includes(item.uid);
              const identity = resolveUserIdentity(item);
              return (
                <Box
                  key={`suggestion-${item.uid}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    p: 1,
                    borderRadius: "12px",
                    bgcolor: onSurface(0.03),
                    border: `1px solid ${onSurface(0.07)}`,
                  }}
                >
                  <Avatar
                    src={identity.photo}
                    sx={{ width: 40, height: 40, cursor: "pointer" }}
                    onClick={() => handleOpenUserProfile(item.uid)}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="subtitle2"
                      noWrap
                      sx={{ fontWeight: 700, cursor: "pointer" }}
                      onClick={() => handleOpenUserProfile(item.uid)}
                    >
                      {identity.name}
                    </Typography>
                    <Typography variant="caption" noWrap sx={{ color: onSurface(0.55), display: "block" }}>
                      {item.mutualCount} {t("mutualFollowing")}
                    </Typography>
                  </Box>
                  <Button
                    onClick={() => toggleFollowUser(item.uid)}
                    disabled={followLoadingUid === item.uid}
                    sx={{
                      textTransform: "none",
                      minWidth: 90,
                      borderRadius: "10px",
                      fontWeight: 600,
                      bgcolor: iFollow ? onSurface(0.1) : "#00d4ff",
                      color: iFollow ? onSurface(1) : "#001217",
                      "&:hover": {
                        bgcolor: iFollow ? onSurface(0.18) : "#00b8d9",
                      },
                    }}
                  >
                    {followLoadingUid === item.uid ? <CircularProgress size={16} /> : iFollow ? t("followed") : getFollowLabel(item.uid)}
                  </Button>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Dialog>

      {(() => {
        const livePost = userPosts.find((p) => p.id === selectedPost?.id) || selectedPost;
        const hasLiked = livePost?.likes?.includes(currentUser?.uid);
        const hasSaved = livePost?.savedBy?.includes(currentUser?.uid);
        const primaryMedia = getPrimaryMedia(livePost);
        const isVideo = primaryMedia?.type === "video";

        return (
          <Dialog
            open={openPostDialog}
            onClose={() => setOpenPostDialog(false)}
            fullWidth
            maxWidth="sm"
            PaperProps={{
              sx: {
                bgcolor: dialogBg,
                color: onSurface(1),
                borderRadius: "16px",
                backgroundImage: dialogGradient,
                overflow: "hidden",
              },
            }}
          >
            {livePost && (
              <>
                <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 1.5, borderBottom: `1px solid ${onSurface(0.08)}` }}>
                  <Avatar src={livePost.userImg || profile.photoURL} sx={{ width: 38, height: 38, mr: 1.5 }} />
                  <Box flex={1}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                        {livePost.username || profile.displayName}
                        {(profile.verified || livePost.userVerified) ? <Verified sx={{ fontSize: 15, color: "#35b6ff" }} /> : null}
                      </Box>
                    </Typography>
                    {livePost.handle && (
                      <Typography variant="caption" sx={{ color: onSurface(0.45) }}>
                        @{livePost.handle}
                      </Typography>
                    )}
                  </Box>
                  <IconButton onClick={() => setOpenPostDialog(false)} sx={{ color: onSurface(0.7) }}>
                    <Close />
                  </IconButton>
                </Box>

                {primaryMedia?.url && (
                  <Box
                    component={isVideo ? "video" : "img"}
                    src={primaryMedia.url}
                    alt={isVideo ? undefined : t("postImageAlt")}
                    controls={Boolean(isVideo)}
                    muted={Boolean(isVideo)}
                    playsInline={Boolean(isVideo)}
                    sx={{ width: "100%", height: "auto", maxHeight: "400px", display: "block", objectFit: "cover", bgcolor: "#000" }}
                  />
                )}

                <Box sx={{ px: 2, py: 1.5 }}>
                  <PostTextBlock text={livePost.text} textBackground={livePost.textBackground} isLightTheme={isLightTheme} />
                </Box>

                <Divider sx={{ bgcolor: onSurface(0.08) }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1, py: 0.5 }}>
                  <Stack direction="row" alignItems="center" spacing={0.2}>
                    <IconButton onClick={handleLikePost} sx={{ color: hasLiked ? "#ff4b4b" : onSurface(0.8), p: 0.45 }}>
                      {hasLiked ? <Favorite fontSize="small" /> : <FavoriteBorder fontSize="small" />}
                    </IconButton>
                    <Typography variant="caption" sx={{ mr: 0.35, fontSize: "0.68rem" }}>{livePost.likes?.length || 0}</Typography>
                    <IconButton onClick={() => setOpenComentariosPost(true)} sx={{ color: onSurface(0.8), p: 0.45 }}>
                      <ChatBubbleOutline fontSize="small" />
                    </IconButton>
                    <Typography variant="caption" sx={{ mr: 0.35, fontSize: "0.68rem" }}>{livePost.commentsCount || 0}</Typography>
                    <IconButton onClick={handleRepost} sx={{ color: onSurface(0.8), p: 0.45 }}>
                      <Repeat fontSize="small" />
                    </IconButton>
                    <Typography variant="caption" sx={{ color: onSurface(0.8), mr: 0.35, fontSize: "0.68rem" }}>
                      {livePost.repostCount || 0}
                    </Typography>
                    <IconButton onClick={() => setOpenSendPostDialog(true)} sx={{ color: onSurface(0.8), p: 0.45 }}>
                      <Send fontSize="small" />
                    </IconButton>
                    <Typography variant="caption" sx={{ color: onSurface(0.8), fontSize: "0.68rem" }}>
                      {livePost.sentCount || 0}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.2}>
                    <IconButton onClick={handleSavePost} sx={{ color: hasSaved ? "#00d4ff" : onSurface(0.8), p: 0.45 }}>
                      {hasSaved ? <Bookmark fontSize="small" /> : <BookmarkBorder fontSize="small" />}
                    </IconButton>
                    <IconButton onClick={() => handleSharePost(livePost)} sx={{ color: onSurface(0.8), p: 0.45 }}>
                      <IosShare fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </>
            )}
          </Dialog>
        );
      })()}

      <Comentarios
        open={openComentariosPost}
        handleClose={() => setOpenComentariosPost(false)}
        postId={selectedPost?.id}
        onOpenProfile={onOpenProfile}
        language={language}
        themeMode={themeMode}
      />

      <Dialog
        open={openSendPostDialog}
        onClose={() => !sendingPostUid && setOpenSendPostDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            borderRadius: "16px",
            backgroundImage: dialogGradient,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{t("sendToContacts")}</DialogTitle>
        <DialogContent>
          {followingContacts.length === 0 ? (
            <Typography sx={{ color: onSurface(0.65), mt: 1 }}>
              {t("noContacts")}
            </Typography>
          ) : (
            <Stack spacing={1} sx={{ mt: 1 }}>
              {followingContacts.map((contact) => {
                const identity = resolveUserIdentity(contact);
                return (
                  <Box
                    key={contact.uid}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.2,
                      p: 1,
                      borderRadius: "12px",
                      bgcolor: onSurface(0.03),
                      border: `1px solid ${onSurface(0.07)}`,
                    }}
                  >
                    <Avatar src={identity.photo} sx={{ width: 38, height: 38 }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
                        {identity.name}
                      </Typography>
                      <Typography variant="caption" noWrap sx={{ color: onSurface(0.55), display: "block" }}>
                        @{identity.handle || t("usernameFallback")}
                      </Typography>
                    </Box>
                    <Button
                      onClick={() => handleSendPostToContact(contact.uid)}
                      disabled={sendingPostUid === contact.uid}
                      sx={{
                        textTransform: "none",
                        minWidth: 88,
                        borderRadius: "10px",
                        fontWeight: 600,
                        bgcolor: "#00d4ff",
                        color: "#001217",
                        "&:hover": { bgcolor: "#00b8d9" },
                      }}
                    >
                      {sendingPostUid === contact.uid ? <CircularProgress size={16} /> : t("send")}
                    </Button>
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setOpenSendPostDialog(false)}
            disabled={Boolean(sendingPostUid)}
            sx={{ color: onSurface(0.75), textTransform: "none" }}
          >
            {t("close")}
          </Button>
        </DialogActions>
      </Dialog>

        <Snackbar
          open={toast.open}
          autoHideDuration={2600}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setToast((prev) => ({ ...prev, open: false }))}
            severity={toast.type}
            sx={{ width: "100%" }}
            variant="filled"
          >
            {toast.text}
          </Alert>
        </Snackbar>
    </Box>
  );
};

export default PerfilUsuario;
